const wishlistName = 'main_wishlist';
if (!window.dAgency) window.dAgency = {};
if (!window.dAgency.wishlistCache) window.dAgency.wishlistCache = null;

/**
 * Intercepts property assignment on `obj[prop]` using Object.defineProperty.
 * Calls `callback(newValue)` the moment the property is set.
 * Restores the property as a plain writable property after the assignment.
 *
 * @param {Object} obj     - The object to watch.
 * @param {string} prop    - The property name to intercept.
 * @param {Function} callback - Called with the new value on assignment.
 */
const _watchProperty = (obj, prop, callback) => {
  let currentValue = obj[prop];
  Object.defineProperty(obj, prop, {
    configurable: true,
    enumerable: true,
    get() {
      return currentValue;
    },
    set(value) {
      currentValue = value;
      // Restore to a plain writable property so the SDK can use it normally
      Object.defineProperty(obj, prop, {
        configurable: true,
        enumerable: true,
        writable: true,
        value: currentValue,
      });
      callback(currentValue);
    },
  });
};

// Singleton promise: shared across all callers so only one watcher is ever created.
// Once resolved it stays resolved — subsequent callers get an already-resolved Promise.
let _swymReadyPromise = null;

/**
 * Waits for the Swym SDK to be fully operational (i.e. `window._swat.fetchLists`
 * is a callable function).
 *
 * Strategy (zero polling overhead):
 *  1. If already ready → return resolved Promise immediately.
 *  2. Return the existing singleton Promise if already waiting.
 *  3. Otherwise, set up reactive watchers via Object.defineProperty:
 *     - If `_swat` doesn't exist yet: watch `window._swat` assignment, then
 *       watch `_swat.fetchLists` assignment.
 *     - If `_swat` is a stub (exists but fetchLists not yet set): watch
 *       `_swat.fetchLists` directly.
 *  4. If Object.defineProperty fails for any reason, fall back to rAF polling.
 *
 * IMPORTANT: must be defined BEFORE waitForSwym which references it.
 *
 * @returns {Promise<void>}
 */
const waitForSwymReady = () => {
  if (typeof window._swat?.fetchLists === 'function') return Promise.resolve();
  if (_swymReadyPromise) return _swymReadyPromise;

  _swymReadyPromise = new Promise((resolve) => {
    const done = () => resolve();

    try {
      if (!window._swat) {
        // _swat doesn't exist yet: watch its assignment on window
        _watchProperty(window, '_swat', (swat) => {
          if (typeof swat?.fetchLists === 'function') {
            done(); // fetchLists already present on the new _swat
          } else if (swat) {
            _watchProperty(swat, 'fetchLists', done); // wait for fetchLists too
          }
        });
      } else {
        // _swat exists but is a stub: watch fetchLists assignment
        _watchProperty(window._swat, 'fetchLists', done);
      }
    } catch (e) {
      console.error('Error while waiting for Swym SDK to be fully operational: ', { cause: e });
      // Object.defineProperty failed (non-configurable property): fall back to rAF polling
      const start = Date.now();
      const check = () => {
        if (typeof window._swat?.fetchLists === 'function') {
          done();
        } else if (Date.now() - start < 60000) {
          requestAnimationFrame(check);
        }
      };
      requestAnimationFrame(check);
    }
  });

  return _swymReadyPromise;
};

/**
 * Waits for the Swym SDK (`window._swat`) to be ready.
 * Returns a Promise that resolves once `_swat` is fully operational.
 * Uses SwymCallbacks as primary channel; falls back to waitForSwymReady
 * in case SwymCallbacks was already consumed (e.g. late cookie consent).
 */
const waitForSwym = () => {
  return new Promise((resolve) => {
    if (typeof window._swat?.fetchLists === 'function') {
      resolve(); // already ready
      return;
    }
    if (!window.SwymCallbacks) window.SwymCallbacks = [];
    // Push resolve function into SwymCallbacks to execute when SDK is loaded
    window.SwymCallbacks.push(resolve);
    // Fallback: if SwymCallbacks is never flushed (late consent), use reactive watcher
    waitForSwymReady().then(resolve);
  });
};

/**
 * Creates a new wishlist with the name defined in `wishlistName`.
 * Caches the result in `cachedWishlist` to avoid redundant calls.
 *
 * @returns {Promise<Object>} Resolves with the new wishlist object.
 */
const createWishlist = async () => {
  await waitForSwym();
  return new Promise((resolve, reject) => {
    window._swat.createList(
      { lname: wishlistName },
      function (newList) {
        window.dAgency.wishlistCache = newList;
        document.dispatchEvent(new CustomEvent('dAgency:wishlist:updated', { detail: { wishlist: newList } }));
        resolve(newList);
      },
      function (error) {
        reject(new Error('Error while creating a List: ', { cause: error }));
      },
    );
  });
};

let wishlistPromise = null;

/**
 * Retrieves the wishlist with the name defined in `wishlistName`.
 * Uses cached value if available.
 * If wishlist does not exist, creates a new one.
 * Uses a promise-based lock to prevent multiple simultaneous Swym API calls.
 *
 * @returns {Promise<Object>} Resolves with the wishlist object.
 */
const getWishlist = async () => {
  await waitForSwym();

  if (window.dAgency.wishlistCache) return window.dAgency.wishlistCache;

  if (wishlistPromise) return wishlistPromise;

  wishlistPromise = new Promise((resolve, reject) => {
    window._swat.fetchLists({
      callbackFn: async function (lists) {
        try {
          const foundList = lists.find((list) => list.lname == wishlistName);
          if (foundList) {
            window.dAgency.wishlistCache = foundList;
            document.dispatchEvent(new CustomEvent('dAgency:wishlist:updated', { detail: { wishlist: foundList } }));
            resolve(foundList);
            return;
          }
          const newlist = await createWishlist();
          window.dAgency.wishlistCache = newlist;
          resolve(newlist);
        } catch (error) {
          reject(error);
        } finally {
          wishlistPromise = null;
        }
      },
      errorFn: function (error) {
        wishlistPromise = null;
        reject(new Error('Error while fetching all Lists: ', { cause: error }));
      },
    });
  });

  return wishlistPromise;
};

/**
 * Adds a product to the wishlist.
 * Updates the button status after successful addition.
 *
 * @param {Object} options - Product details and UI button reference.
 * @returns {Promise<Object|boolean>} Resolves with the added product object, or `false` on error.
 */
const addToWishlist = async ({
  variantID = '',
  masterID = '',
  productURL = '',
  compareAtPrice = '',
  price = '',
  button = null,
} = {}) => {
  if (!variantID || !masterID || !productURL || !button) {
    console.error('Missing required parameters to add product to wishlist');
    return false;
  }

  const wishlist = await getWishlist();
  if (!wishlist) return;

  if (wishlist.lid) {
    return new Promise((resolve, reject) => {
      window._swat.addToList(
        wishlist.lid,
        {
          epi: variantID,
          empi: masterID,
          du: productURL,
          cprops: { compare_at_price: compareAtPrice, price: price },
        },
        function (addedListItem) {
          if (!wishlist.listcontents) wishlist.listcontents = [];
          wishlist.listcontents.push(addedListItem);
          updateWishlistBtn(button, wishlist);
          document.dispatchEvent(
            new CustomEvent('dAgency:wishlist:updated', { detail: { wishlist, added: addedListItem } }),
          );
          resolve(addedListItem);
        },
        function (error) {
          reject(new Error('Error while adding product to wishlist: ', { cause: error }));
        },
      );
    });
  } else {
    console.error('wishlist.lid is not defined while trying to add product');
    return false;
  }
};

/**
 * Delete a product (or all variants) from the wishlist
 * @param {Object} params
 * @param {string} params.variantID - The variant ID to delete
 * @param {string} params.masterID - The master product ID
 * @param {string} params.productURL - The product URL
 * @param {HTMLElement} params.button - The button that triggered the deletion
 * @returns {Promise<boolean>} - Returns true if deletion succeeded, false otherwise
 */
const deleteFromWishlist = async ({ variantID = '', masterID = '', productURL = '', button = null } = {}) => {
  // Validate required parameters
  if (!variantID || !masterID || !productURL || !button) {
    console.error('Missing required parameters to delete product from wishlist');
    return false;
  }

  const wishlist = await getWishlist();
  if (!wishlist || !wishlist.listcontents) return false;

  const forceAllVariants = button?.dataset?.forceAllVariants === 'true';

  if (!wishlist.lid) {
    console.warn('wishlist.lid is not defined while trying to delete product');
    return false;
  }

  // Helper: wrap callback-based deleteFromList into a promise
  const deleteItem = (item) =>
    new Promise((resolve, reject) => {
      window._swat.deleteFromList(wishlist.lid, { epi: item.epi, empi: item.empi, du: item.du }, resolve, reject);
    });

  try {
    if (forceAllVariants) {
      // Collect all items belonging to this master product
      const itemsToDelete = wishlist.listcontents.filter((p) => p.empi == masterID);

      // Remove duplicates just in case
      const uniqueItems = Array.from(
        new Map(itemsToDelete.map((item) => [`${item.epi}-${item.empi}-${item.du}`, item])).values(),
      );

      // Delete all matching items concurrently
      await Promise.all(uniqueItems.map(deleteItem));
    } else {
      // Delete single specific variant
      await deleteItem({ epi: variantID, empi: masterID, du: productURL });
    }

    // Update local wishlist state
    wishlist.listcontents = wishlist.listcontents.filter((p) => {
      const matchVariant = p.epi == variantID;
      const matchMaster = p.empi == masterID;
      return forceAllVariants ? matchMaster !== true : !(matchVariant && matchMaster);
    });

    updateWishlistBtn(button, wishlist);
    document.dispatchEvent(new CustomEvent('dAgency:wishlist:updated', { detail: { wishlist } }));

    return true;
  } catch (error) {
    console.error('Error while deleting product from wishlist:', error);
    return false;
  }
};

/**
 * Removes a stale item from the Swym wishlist (no UI button required).
 * Used to cleanup entries whose underlying Shopify product no longer exists,
 * keeping the Swym counter consistent with what can actually be rendered.
 *
 * @param {Object} item - Swym item identifiers.
 * @param {string|number} item.epi - Variant ID.
 * @param {string|number} item.empi - Product (master) ID.
 * @param {string} item.du - Product deep URL stored in Swym.
 * @returns {Promise<boolean>} Resolves true on success, false otherwise.
 */
const removeStaleWishlistItem = async ({ epi, empi, du } = {}) => {
  const wishlist = await getWishlist();
  if (!wishlist?.lid) return false;

  return new Promise((resolve) => {
    window._swat.deleteFromList(
      wishlist.lid,
      { epi, empi, du },
      () => {
        if (wishlist.listcontents) {
          wishlist.listcontents = wishlist.listcontents.filter((p) => !(p.epi == epi && p.empi == empi));
        }
        document.dispatchEvent(new CustomEvent('dAgency:wishlist:updated', { detail: { wishlist } }));
        resolve(true);
      },
      (error) => {
        console.error('Error while removing stale item from wishlist:', error);
        resolve(false);
      },
    );
  });
};

/**
 * Updates all wishlist buttons on the page.
 * - Removes data-wishlist-blocking unconditionally (as soon as Swym is ready).
 * - Sets data-wishlist-status to 'inactive' on all buttons when listcontents
 *   is absent or empty (handles empty wishlists correctly).
 * - Sets 'active'/'inactive' per product when listcontents has entries.
 */
const updateAllWishlistBtns = async () => {
  const wishlist = await getWishlist();

  // Collect all wishlist buttons on the page
  const buttons = [...document.querySelectorAll('[data-wishlist-btn]')];

  // Always remove blocking attribute as soon as Swym is ready
  buttons.forEach((button) => button.removeAttribute('data-wishlist-blocking'));

  // If wishlist has no contents, mark all buttons inactive and stop
  if (!wishlist?.listcontents?.length) {
    buttons.forEach((btn) => (btn.dataset.wishlistStatus = 'inactive'));
    return;
  }

  const groups = new Map();

  // Group buttons by product (variantId + productId)
  buttons.forEach((button) => {
    const variantId = button?.dataset?.wishlistVariantId;
    const productId = button?.dataset?.wishlistProductId;
    const key = `${variantId}-${productId}`;

    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(button);
  });

  // Update status for each group while respecting each button's forceAllVariants
  groups.forEach((btns, key) => {
    const [variantId, productId] = key.split('-');

    // Iterate over all buttons in the group individually
    btns.forEach((btn) => {
      const forceAllVariants = btn.dataset?.forceAllVariants === 'true';

      // Check if the product or variant exists in the wishlist:
      // - if forceAllVariants is true, match product only (empi)
      // - otherwise, match specific variant of the product (epi + empi)
      const inWishlist = wishlist.listcontents?.some((p) => {
        const matchVariant = p.epi == variantId;
        const matchMaster = p.empi == productId;

        return forceAllVariants ? matchMaster : matchVariant && matchMaster;
      });

      // Set button status based on whether it's in the wishlist
      btn.dataset.wishlistStatus = inWishlist ? 'active' : 'inactive';
    });
  });
};

/**
 * Updates a single wishlist button based on variantID + productID
 * If no wishlist is provided, fetches the current wishlist.
 */
const updateWishlistBtn = async (button, wishlistGiven = null) => {
  if (!button) return;

  // Use provided wishlist or fetch the current one
  const wishlist = wishlistGiven || (await getWishlist());
  if (!wishlist) return;

  const forceAllVariants = button?.dataset?.forceAllVariants === 'true';

  button.removeAttribute('data-wishlist-blocking');

  const variantId = button?.dataset?.wishlistVariantId;
  const productId = button?.dataset?.wishlistProductId;

  // Check if the product exists in the wishlist
  const inWishlist = wishlist.listcontents?.find((p) => {
    const matchVariant = p.epi == variantId;
    const matchMaster = p.empi == productId;

    return forceAllVariants ? matchMaster : matchVariant && matchMaster;
  });

  const status = inWishlist ? 'active' : 'inactive';

  // Update all buttons referring to the same product
  document
    .querySelectorAll(`[data-wishlist-variant-id="${variantId}"][data-wishlist-product-id="${productId}"]`)
    .forEach((btn) => {
      btn.dataset.wishlistStatus = status;
      const accessibleAttributes = ['aria-label', 'title'];
      accessibleAttributes.forEach((attribute) => {
        btn.setAttribute(
          attribute,
          btn.dataset.wishlistStatus === 'active' ? btn.dataset.wishlistRemoveLabel : btn.dataset.wishlistAddLabel,
        );
      });
    });
};

/**
 * Observes DOM mutations to handle buttons added dynamically via
 * Section Rendering API or other async patterns. When new [data-wishlist-btn]
 * elements appear and Swym is ready, triggers a full wishlist update.
 */
const observeNewWishlistBtns = () => {
  let debounceTimer;

  const observer = new MutationObserver((mutations) => {
    // Only react if Swym is operational and new wishlist buttons were added
    if (typeof window._swat?.fetchLists !== 'function') return;

    const hasNewBtn = mutations.some((m) =>
      [...m.addedNodes].some(
        (node) =>
          node.nodeType === Node.ELEMENT_NODE &&
          (node.matches('[data-wishlist-btn]') || node.querySelector('[data-wishlist-btn]')),
      ),
    );

    if (hasNewBtn) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => updateAllWishlistBtns(), 50);
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
};

/**
 * Renders the wishlist item count into all `.swym-wishlist-header-counter` elements.
 * Must be called with a ready `_swat` instance (passed by SwymCallbacks or manually).
 * Ref: https://support.getswym.com/support/solutions/articles/9000238499-wishlist-header-counter-or-fave-counter
 *
 * @param {Object} swat - The Swym SDK instance (window._swat).
 */
const renderWishlistCounter = (swat) => {
  const counterElements = document.querySelectorAll('.swym-wishlist-header-counter');
  if (!counterElements.length) return;
  if (typeof swat?.renderWishlistCount === 'function') {
    counterElements.forEach((counterElement) => {
      swat.renderWishlistCount(counterElement, function (count, element) {
        // Use this for custom rendering logic (e.g. hide counter when 0)
        // render count with parenthesis
        element.textContent = `(${count})`;
      });
    });
  }
};

/**
 * Initializes wishlist logic on page load.
 * - SwymCallbacks: primary path (consent accepted immediately).
 * - waitForSwymReady fallback: handles late cookie consent via reactive watchers.
 * - MutationObserver: handles buttons added via Section Rendering API.
 */
const initFirstWishlist = () => {
  if (!window.SwymCallbacks) window.SwymCallbacks = [];

  // Register counter render alongside the button update
  window.SwymCallbacks.push((swat) => {
    updateAllWishlistBtns();
    renderWishlistCounter(swat); // swat is passed by SwymCallbacks flush
  });

  // Fallback for late cookie consent
  waitForSwymReady().then(() => {
    updateAllWishlistBtns();
    renderWishlistCounter(window._swat); // _swat is guaranteed ready here
  });

  observeNewWishlistBtns();

  // Re-render counter on every wishlist mutation
  document.addEventListener('dAgency:wishlist:updated', () => {
    updateAllWishlistBtns();
    renderWishlistCounter(window._swat);
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted || window.dAgency.wishlistCache) {
      window.dAgency.wishlistCache = null;
      updateAllWishlistBtns();
      renderWishlistCounter(window._swat); // refresh counter on back navigation
    }
  });
};

export {
  addToWishlist,
  deleteFromWishlist,
  getWishlist,
  createWishlist,
  initFirstWishlist,
  removeStaleWishlistItem,
  renderWishlistCounter,
  updateAllWishlistBtns,
  updateWishlistBtn,
  wishlistName,
};
